import logger from '../../../utils/logger';
import { DeviceStatus } from '../../../constants/DeviceStatus';
import { OUTPUT_TTL } from '../../../constants/Pac';
import { ScaleTypes } from '../../discharge-store';
import { timerKey } from '../../../utils/timer-manager/timer-manager';
import {
  getSecondWaterPump,
  getWaterPump,
  isHeadWaterNotDischarged,
} from '../water-pump-checks';
import {
  BatchAction,
  MeteredMaterialType,
} from '../../../constants/MaterialModels';
import { VibratorMode } from '../../../constants/DeviceModels';
import moment from 'moment';
import { reaction } from 'mobx';
import { beltSwitch } from '../device-switches/belt-switch';
import { vibratorSwitch } from '../device-switches/vibrator-switch';
import { RemoteDisplayBatchStatus } from '../../../constants/RemoteDisplayBatchStatus';
import { controlFlowRate } from '../control-flow-rate';
import { aeratorSwitch } from '../device-switches/aerator-switch';
import { screwSwitch } from '../device-switches/screw-switch';
import { dustCollectorSwitch } from '../device-switches/dust-collector-switch';
import { truckSwitch } from '../device-switches/truck-switch';
import { BatchCycleStatus } from '../../../constants/BatchCycleStatus';
import { checkIfDischargeStartPercentForPumpsReady } from '../checkAdmixtureDischargeConditions';
import { capitalizeWord } from '../../../utils/text-transform';

import type { IBatchStore } from '../batch-store-types';
import type { IBatchData } from '../../../containers/Batch/IBatchData';
import type { IMeteredMaterialModel } from '../../../../types/material/MaterialModels';
import type { IScale } from '../../../../types/device/DeviceModels';

export const dischargeMixin = {

  /**
   * Discharge final scale
   */
  dischargeScale(
    this: IBatchStore,
    scale: any,
    runningData: any,
    onChange: (scale: any, reaction?: any) => void = () => {},
  ) {
    let d = this.getDeviceStatus(scale.id);

    logger.log('discharge scale', scale.id, scale.description, scale.type, scale.target, d.status);

    if (!runningData) {
      return;
    }

    if (runningData.finalScalesDischarged.includes(scale.id)) {
      logger.log('scale already discharged:', scale.id, scale.description, scale.type);
      return;
    }

    if (
      d.status !== DeviceStatus.WAIT_DISCH
      && d.status !== DeviceStatus.WAIT_WATER
      && d.status !== DeviceStatus.WAIT_ADMIX
      && d.status !== DeviceStatus.DISCH_DELAY
    ) {
      logger.log('scale not ready for discharge', scale.id, scale.description, d.status);
      return;
    }

    if (!scale.decumulative && scale.target <= 0) {
      return;
    }

    const headWaterShouldWait = scale.type === ScaleTypes.WATER
      && (
        (scale.dischargeIsStop && !scale.dischargeFinish && !scale.dischargeIsResume)
        ||
        (runningData.headWater && !runningData.headWaterScaleDischarged && !scale.dischargeFinish)
      )
      && !this.isDischargeAdmixBeforeHeadWaterCompleted(runningData);

    const washWaterShouldWait = scale.type === ScaleTypes.WATER
      && (
        (scale.dischargeIsStop && !scale.dischargeFinish && !scale.dischargeIsResume)
        ||
        ((!runningData.headWater || runningData.headWaterScaleDischarged) && runningData.washWater && !scale.dischargeFinish)
      )
      && !this.isDischargeAdmixBeforeWashWaterCompleted(runningData);

    this.timers.clear(timerKey.dischargeDelay(scale.id));

    if (headWaterShouldWait || washWaterShouldWait) {
      logger.log(`scale ${scale.description || scale.id} ${scale.type} wait for admix`, headWaterShouldWait, washWaterShouldWait);

      const conflictedAdmixWithWater = this.getConflictedAdmixWithWater(runningData, headWaterShouldWait, washWaterShouldWait);
      if (conflictedAdmixWithWater) {
        logger.warn(`discharge sequence conflict - water scale ${scale.type} wait for admix ${conflictedAdmixWithWater.itemCode}`);

        this.showError(`Discharge sequence conflict - scale ${scale.description}
         is waiting for admix ${conflictedAdmixWithWater.itemCode} while ${conflictedAdmixWithWater.itemCode} is waiting for the scale`);

        const scaleActionType = !runningData.batchActionFired.includes(conflictedAdmixWithWater.batchStart)
          ? conflictedAdmixWithWater.batchStart
          : conflictedAdmixWithWater.dischargeStart;

        if (scaleActionType) {
          logger.log(`forcing scale action: ${scaleActionType} for conflicted admix: ${conflictedAdmixWithWater.itemCode}
          ${scale.description}`);
          this.onBatchAction(runningData, scaleActionType);
        }
      }

      this.timers.set(timerKey.dischargeDelay(scale.id), () => {
        this.dischargeScale(scale, runningData, onChange);
      }, 2000);
      return;
    }

    const scaleWithDischargeOptions = runningData.sequence.discharges.find((v: { scaleId: number }) => v.scaleId === scale.id);
    const shouldWaitForAdmix = scaleWithDischargeOptions?.waitForAdmix;
    const scaleShouldWaitForAdmix = shouldWaitForAdmix && !this.areAllAdmixturesFinishDischarge(runningData);

    if (scaleShouldWaitForAdmix) {
      logger.log(`scale ${scale.description || scale.id} ${scale.type} wait for admix`, scaleWithDischargeOptions);

      const conflictedAdmix = this.getConflictedAdmixWithScale(runningData, scale.type);
      if (conflictedAdmix) {
        logger.warn(`discharge sequence conflict - scale ${scale.type} wait for admix ${conflictedAdmix.itemCode}`);

        this.showError(`Discharge sequence conflict - scale ${scale.description}
         is waiting for admix ${conflictedAdmix.itemCode} while ${conflictedAdmix.itemCode} is waiting for the scale`);

        const scaleActionType = this.getActionTypeForScale(scale);

        if (scaleActionType) {
          logger.log(`forcing scale discharge start for conflicted admix: ${conflictedAdmix.itemCode}
           ${scaleActionType} ${scale.description}`);
          this.onBatchAction(runningData, scaleActionType);
        }
      }

      if (!scale.decumulative) {
        this.deviceStatus.set(scale.id, {
          ...d,
          status: DeviceStatus.WAIT_ADMIX,
        });
      }

      this.timers.set(timerKey.dischargeDelay(scale.id), () => {
        this.dischargeScale(scale, runningData, onChange);
      }, 2000);
      return;
    }

    let dischargeOpenDelay = scale.delay || scale.dischargeOpenDelay || 0;
    this.discharging = true;

    if (scale.switchGateTypePNo) {
      const mixer = this.devicesStore.mixers.find((v: { diverterPNo: number }) => v.diverterPNo === scale.switchGateTypePNo);

      if (mixer) {
        const isOutputOn = this.isOutputOn(mixer.diverterPNo);
        dischargeOpenDelay = Math.max(dischargeOpenDelay, 1);

        logger.log(`scale #${scale.id} ${scale.type} with switch gate, checking mixer #${mixer.id}`,
          `for mixer #${runningData.mixerId} before discharge, output ${mixer.diverterPNo} on?:`, isOutputOn,
          'discharge delay:', dischargeOpenDelay);

        if (runningData.mixerId && isOutputOn) {
          this.setOutput(mixer.diverterPNo, 0);
        } else if (!runningData.mixerId && !isOutputOn) {
          this.setOutput(mixer.diverterPNo, OUTPUT_TTL);
        }

      }
    }

    const secondaries = runningData.sequence.discharges.filter((v: { isPrimary: boolean }) => !v.isPrimary);

    // case when water and other scale with withWater = false has start === 0
    const waterScale = secondaries.find((v: { type: string }) => v.type === ScaleTypes.WATER);
    const waterWeight = waterScale ? this.getScaleWeight(waterScale) : 0;
    const shouldWait = !scale.withWater
      && scale.start === 0
      && runningData.headWater > 0
      && !scale.dischargeIsResume
      && waterScale
      && runningData.finalScales[waterScale.id]
      && waterScale.start === 0
      && !waterScale.dischargeIsStop
      && !waterScale.dischargeFinish;
    const waterShouldStop =  waterScale
      && runningData.finalScales[waterScale.id]
      && runningData.washWater > 0
      && waterWeight <= runningData.washWater
      && !waterScale.dischargeIsStop
      && !waterScale.dischargeFinish;
    const waterPump = getWaterPump(this.rootStore, runningData) || {};
    const secondWaterPump = getSecondWaterPump(this.rootStore, runningData) || {};
    const shouldWaitForWaterPump = !scale.withWater
      && (waterPump.id || secondWaterPump.id)
      && runningData.headWater > 0
      && !runningData.headWaterPumpDischarged;
    const shouldWaitForWaterTank = !scale.withWater
      && (waterPump.tankId || secondWaterPump.tankId)
      && runningData.headWater > 0
      && !runningData.headWaterTankDischarged;
    if ((shouldWait && !waterShouldStop) || shouldWaitForWaterPump || shouldWaitForWaterTank) {
      logger.log('discharge pause when start === 0', scale.id, scale.type, scale, waterScale, waterPump,
        (shouldWait && !waterShouldStop) || shouldWaitForWaterPump || shouldWaitForWaterTank);

      scale.dischargeIsStop = true;
      this.pauseDischargeScale(scale);
      this.updateScaleDischarge(scale, runningData);
      this.setTimeout(() => {
        onChange(scale);
      }, 100);
      return;
    }

    if (
      runningData.headWater > 0
      && scale.startAtHeadWater > 0
      && !scale.startAtHeadWaterReady
      && isHeadWaterNotDischarged(this.rootStore, runningData)
      && !scale.dischargeIsResume
      && scale.isPrimary
      && scale.type !== ScaleTypes.WATER
    ) {
      logger.log('discharge pause when startAtHeadWater > 0', scale.type, scale.startAtHeadWater);

      scale.dischargeIsStop = true;
      this.pauseDischargeScale(scale);
      this.updateScaleDischarge(scale, runningData);
      this.setTimeout(() => {
        onChange(scale);
      }, 1000);
      return;
    }

    if (scale.decumulative) {
      const data = runningData.scales[scale.id]?.find((v: any) => !v.isFeedFinish);

      if (!data) {
        return;
      }

      const { material } = data;
      const sid = `${scale.id}-${material.code}`;
      const sm = this.getDeviceStatus(sid);

      this.deviceStatus.set(sid, {
        ...sm,
        status: DeviceStatus.DELAY,
      });

    } else {
      this.deviceStatus.set(scale.id, {
        ...d,
        flowRate: scale.flowRate || runningData.sequence.defaultFlowRate,
        status: DeviceStatus.DISCH_DELAY,
      });
    }

    if (scale.beltId && !this.isBeltOn(scale.beltId) && !runningData.isWashout && !this.isTestMode) {
      beltSwitch(this.rootStore, scale.beltId);
      const belt = this.devicesStore.getDeviceById(Number(scale.beltId));
      logger.log(`scale ${scale.description || scale.id} wait for belt`, scale.beltId);

      if (belt?.autoTurnOn === false) {
        this.showWarning('The scale is waiting for the belt to be turned on');
      }

      this.timers.set(timerKey.dischargeDelay(scale.id), () => {
        this.dischargeScale(scale, runningData, onChange);
      }, 1000);

      return;
    }

    this.timers.set(timerKey.dischargeDelay(scale.id), () => {
      if (!this.isActiveDrop(runningData)) {
        return;
      }

      d = this.getDeviceStatus(scale.id);

      if (d.status === DeviceStatus.WAIT_WATER) {
        logger.log('dischargeScale return', scale.id, scale.type, d.status);
        return;
      }

      const noFlowControl = scale.noFlowControl || runningData.sequence?.noFlowControl;
      const initialOpenTime = this.getInitialOpenTimeForScale(scale, noFlowControl);

      this.deviceStatus.set(scale.id, {
        ...d,
        flowRate: scale.flowRate || runningData.sequence.defaultFlowRate,
      });

      if (scale.decumulative) {

        const data = runningData.scales[scale.id]?.find((v: any) => !v.isFeedFinish);

        if (data) {
          this.dischargeDecumScale(data, runningData, scale);
        }

      } else {

        this.deviceStatus.set(scale.id, {
          ...d,
          flowRate: scale.flowRate || runningData.sequence.defaultFlowRate,
          status: DeviceStatus.DISCHARGING,
        });

        if (!runningData.isWashout) {
          aeratorSwitch(this.rootStore, scale.aeratorId);
          screwSwitch(this.rootStore, scale.screwId);
          dustCollectorSwitch(this.rootStore);
          truckSwitch(this.rootStore);
        }

        const scaleActionType = this.getActionTypeForScale(scale);
        const batchStatusType = this.getBatchStatusTypeForScale(scale);

        if (scaleActionType) {
          logger.log(`scale discharge start: ${scale.type} ${scaleActionType} ${scale.description}`);
          this.onBatchAction(runningData, scaleActionType);
        }

        if (batchStatusType) {
          logger.log('batch cycle:', batchStatusType);
          this.context.onBatchCycle(batchStatusType, this.inputs, this.outputs, this.channels);
        }

        this.openScaleWithMode(scale, initialOpenTime);

      }

      const initialAdjustDelay = ((scale.initialAdjustDelay || 0) * 1000);
      logger.log(`${scale.description}, initial adjust delay: ${scale.initialAdjustDelay}`);

      this.timers.set(timerKey.initialAdjustDelay(scale.id), () => {
        const weighedData = scale.decumulative && runningData.scales[scale.id] ? runningData.scales[scale.id][0] : {};
        this.checkDischargeFinish(scale, weighedData, runningData, (scale, reaction) => {
          onChange(scale, reaction);
          checkIfDischargeStartPercentForPumpsReady(this.rootStore, runningData);
        });
      }, initialAdjustDelay);

    }, dischargeOpenDelay * 1000);

  },

  checkDischargeFinish(
    this: IBatchStore,
    scale: any,
    data: any,
    runningData: any,
    onChange: (scale: any, reaction: any) => void,
    onFinish?: (scale: any) => void,
  ): void {
    const { binForDecum, material } = data;
    const isDecum = !!scale.decumulative;
    let weight = this.getScaleWeight(scale);
    let newWeight = 0;
    let vibrator: any;
    const initialOpenTime = isDecum && binForDecum?.initialOpen
      ? binForDecum.initialOpen
      : scale.dischargeOpen;
    let startTime = moment().add((initialOpenTime || 0), 'seconds');
    let endTime;
    let flowRateProbes: number[] = [];

    if (this.isScaleFinishedDischargeEarlier(scale.id) || (weight <= (scale.dischargeTare || 0) || !runningData)) {
      if (onFinish) {
        onFinish(scale);
      }
      return;
    }

    if (scale.vibratorId) {
      vibrator = this.devicesStore.getDeviceById(scale.vibratorId);
    }

    reaction(
      () => this.statusCount,
      (_counter, reaction) => {
        scale = { ...scale, ...this.devicesStore.getDeviceById(scale.id) };
        const vibrationOn = scale.vibrationOn || 5000;
        const dischargeTare = scale.dischargeTare || 0;
        const minimumDischargeRate = scale.minimumDischargeRate || 50;
        let s = this.getDeviceStatus(scale.id);
        const scalePorts = this.getScalePorts(scale, material);

        if (runningData.finalScalesDischarged.includes(scale.id)) {
          reaction.dispose();
          return;
        }

        if (this.isHoldScale(scale.id)) {
          return;
        }

        if (s.status === DeviceStatus.WAIT_WATER) {
          this.resetWeight(scale.inputPNo);
          this.closeScale(scale, scalePorts.openPort, scalePorts.closePort);
          reaction.dispose();
          return;
        }

        if (this.isAbort || !this.isActiveDrop(runningData)) {
          reaction.dispose();
          return;
        }

        if (this.isHoldDischarge || runningData.isHoldLoad) {
          this.holdScale(scale);

          if (scale.beltId && this.isBeltOn(scale.beltId)) {
            beltSwitch(this.rootStore, scale.beltId, false);
          }

          return;
        }

        newWeight = this.getScaleWeight(scale);

        if (vibrator && vibrator.mode !== VibratorMode.MinFlow) {
          if (newWeight <= vibrationOn && newWeight > dischargeTare) {
            vibratorSwitch(this.rootStore, scale.vibratorId);
          } else if (newWeight <= dischargeTare) {
            vibratorSwitch(this.rootStore, scale.vibratorId, false);
          }
        }

        this.deviceStatus.set(scale.id, {
          ...this.getDeviceStatus(scale.id),
          weight: newWeight,
        });

        if (scale.remoteDisplayId) {
          const { remoteDisplayStore } = this.rootStore;
          remoteDisplayStore.setStatus(scale.remoteDisplayId, {
            status: RemoteDisplayBatchStatus.DISCHARGE,
            currentWeight: `${newWeight}`,
          });
        }

        onChange(scale, reaction);

        endTime = moment();
        s = this.getDeviceStatus(scale.id);

        const sm = scale.decumulative && material ? this.getDeviceStatus(`${scale.id}-${material.code}`) : null;

        if ((!isDecum && s.status === DeviceStatus.DISCHARGING) || (isDecum && sm?.status === DeviceStatus.DISCHARGING)) {

          if (startTime <= endTime && ((!isDecum && (!scale.switchGateTypePNo || this.isScaleSwitchGatePortOn(scale))) || isDecum)) {
            const diff = endTime.diff(startTime) / 1000;
            startTime = moment();
            const flowRate = weight - newWeight;
            const hysteresis = Math.max(scale.hysteresis, 0);
            const flowRateProbesSize = Math.max(Math.round((hysteresis * 10) / 2), 5);

            if (!isDecum || (isDecum && material.code)) {

              if (flowRateProbes.length < flowRateProbesSize) {
                flowRateProbes.push(flowRate);
              }

              if (flowRateProbes.length === flowRateProbesSize && !runningData.isWashout) {
                const averageFlowRate = Math.round(flowRateProbes.reduce((a, b) => a + b, 0) / (flowRateProbes.length/5));

                this.deviceStatus.set(scale.id, {
                  ...this.getDeviceStatus(scale.id),
                  weight: newWeight,
                  currentFlowRate: averageFlowRate,
                });

                logger.log(`scale #${scale.id}, weight diff:`, weight - newWeight, 'floatRate: ', averageFlowRate, 'diff time: ', diff, 'hysteresis:', hysteresis, ' flow rate probes: ', flowRateProbes);

                if (hysteresis) {
                  controlFlowRate(this.rootStore, scale, scalePorts, averageFlowRate, binForDecum, runningData);
                }

                if (vibrator?.mode === VibratorMode.MinFlow) {
                  if (newWeight <= dischargeTare) {
                    vibratorSwitch(this.rootStore, scale.vibratorId, false);
                  } else if (averageFlowRate < minimumDischargeRate) {
                    vibratorSwitch(this.rootStore, scale.vibratorId);
                  } else {
                    vibratorSwitch(this.rootStore, scale.vibratorId, false);
                  }
                }

                flowRateProbes = [];
              }
            }

            weight = newWeight;

          }

        }

        const tolerance = this.getDecumTolerance(scale);

        // TODO: what if weight is negative?
        if (this.isScaleFinishedDischargeEarlier(scale.id)
          || (newWeight <= dischargeTare || (isDecum && (newWeight - tolerance <= s.totalFinalTarget)))) {
          reaction.dispose();
          if (onFinish) {
            onFinish(scale);
          }
        }
      },
    );
  },

  areAllAdmixturesFinishDischarge(runningData: any): boolean {
    const pumpsFinish: number[] = [];
    const allAdmix: number[] = [];

    if (runningData?.pumps) {
      Object.keys(runningData.pumps).forEach((pumpId: number|string) => {
        pumpId = Number(pumpId);
        const isAdmix = runningData.pumps[pumpId]?.materialDetails?.type === MeteredMaterialType.Admixture;

        if (isAdmix) {
          allAdmix.push(pumpId);

          if (runningData.pumps[pumpId].isDischarged) {
            pumpsFinish.push(pumpId);
          }
        }

      });

      const allFinish = allAdmix.length === pumpsFinish.length;
      logger.log('check all admix finish discharge:', allFinish, runningData.uid, allAdmix, pumpsFinish);

      return allFinish;
    }

    return true;
  },

  getConflictedAdmixWithScale(this: IBatchStore, runningData: IBatchData, scaleType: string): null | IMeteredMaterialModel {
    let conflictedAdmix: IMeteredMaterialModel | null = null;
    const scaleTypeCapitalized = capitalizeWord(scaleType);

    if (runningData?.pumps) {
      Object.keys(runningData.pumps).forEach((pumpId: number|string) => {
        pumpId = Number(pumpId);
        const materialDetails = runningData.pumps[pumpId]?.materialDetails;

        if (
          materialDetails?.type === MeteredMaterialType.Admixture
          && (
            [
              `Scale${scaleTypeCapitalized}DischargeStart`,
              `Scale${scaleTypeCapitalized}DischargeComplete`,
            ].includes(materialDetails?.batchStart)
          ||
            [
              `Scale${scaleTypeCapitalized}DischargeStart`,
              `Scale${scaleTypeCapitalized}DischargeComplete`,
            ].includes(materialDetails?.dischargeStart)
          )
        ) {
          conflictedAdmix = materialDetails;
        }

      });

    }

    return conflictedAdmix;
  },

  getConflictedAdmixWithWater(
    this: IBatchStore,
    runningData: IBatchData,
    checkHeadWater: boolean,
    checkWashWater: boolean,
  ): null | IMeteredMaterialModel {
    const pumps = runningData?.pumps;
    if (!pumps) return null;

    for (const pumpId of Object.keys(pumps)) {
      const id = Number(pumpId);
      const materialDetails = pumps[id]?.materialDetails;

      if (materialDetails?.type !== MeteredMaterialType.Admixture) {
        continue;
      }

      if (pumps[id]?.isDischarged) {
        continue;
      }

      const matchesHeadWater =
        runningData.sequence?.holdHeadWaterUntilAdmixFinish &&
        checkHeadWater &&
        (
          materialDetails.batchStart === BatchAction.StartDischargingHeadWater ||
          materialDetails.batchStart === BatchAction.StartDischargingWashWater ||
          materialDetails.batchStart === BatchAction.ScaleWaterDischargeStart ||
          materialDetails.batchStart === BatchAction.ScaleWaterDischargeComplete||
          materialDetails.dischargeStart === BatchAction.StartDischargingHeadWater ||
          materialDetails.dischargeStart === BatchAction.StartDischargingWashWater ||
          materialDetails.dischargeStart === BatchAction.ScaleWaterDischargeStart ||
          materialDetails.dischargeStart === BatchAction.ScaleWaterDischargeComplete
        );

      const matchesWashWater =
        runningData.sequence?.holdWashWaterUntilAdmixFinish &&
        checkWashWater &&
        (
          materialDetails.batchStart === BatchAction.StartDischargingWashWater ||
          materialDetails.batchStart === BatchAction.ScaleWaterDischargeComplete ||
          materialDetails.dischargeStart === BatchAction.StartDischargingWashWater ||
          materialDetails.dischargeStart === BatchAction.ScaleWaterDischargeComplete
        );

      if (matchesHeadWater || matchesWashWater) {
        return materialDetails;
      }
    }

    return null;
  },

  isScaleFinishedDischargeEarlier(this: IBatchStore, scaleId: number): boolean {
    const d = this.getDeviceStatus(scaleId);
    const initialData = d?.uid ? this.running.find((v: any) => v.uid === d.uid && v.currentDrop === d.currentDrop) : null;

    if (initialData && Array.isArray(initialData.scalesFinishedDischargeEarlier)) {
      return initialData.scalesFinishedDischargeEarlier.includes(scaleId);
    }

    return false;
  },

  isDischargeAdmixBeforeWashWaterCompleted(this: IBatchStore, data: IBatchData): boolean {
    if (!data.sequence?.holdWashWaterUntilAdmixFinish) {
      return true;
    }

    return this.isAllAdmixDischarged(data);

  },

  isDischargeAdmixBeforeHeadWaterCompleted(this: IBatchStore, data: IBatchData): boolean {
    if (!data.sequence?.holdHeadWaterUntilAdmixFinish) {
      return true;
    }

    return this.isAllAdmixDischarged(data);

  },

  isAllAdmixDischarged(this: IBatchStore, data: IBatchData): boolean {
    if (!data.pumps) {
      return true;
    }

    let admixToDischarge: number[] = [];
    let admixDischarged: number[] = [];

    Object.keys(data.pumps).forEach(pumpId => {
      const pumpData = data.pumps[pumpId];
      const { pump, materialDetails } = pumpData;

      if (materialDetails.type === MeteredMaterialType.Admixture) {
        admixToDischarge.push(pump.id);

        if (pumpData.isDischarged) {
          admixDischarged.push(pump.id);
        }
      }

    });

    logger.log(`admix to discharge: ${admixToDischarge}, discharged: ${admixDischarged}`);

    return admixDischarged.length === admixToDischarge.length;
  },

  getActionTypeForScale(scale: any) {
    let scaleActionType;

    if (scale.type === ScaleTypes.AGG) {
      scaleActionType = BatchAction.ScaleAggDischargeStart;
    } else if (scale.type === ScaleTypes.CEM) {
      scaleActionType = BatchAction.ScaleCemDischargeStart;
    } else if (scale.type === ScaleTypes.WATER) {
      scaleActionType = BatchAction.ScaleWaterDischargeStart;
    }

    return scaleActionType;
  },

  getBatchStatusTypeForScale(scale: any) {
    let batchStatusType;

    if (scale.type === ScaleTypes.AGG) {
      batchStatusType = BatchCycleStatus.START_AGG_DISCHARGE;
    } else if (scale.type === ScaleTypes.CEM) {
      batchStatusType = BatchCycleStatus.START_CEM_DISCHARGE;
    } else if (scale.type === ScaleTypes.WATER) {
      batchStatusType = BatchCycleStatus.START_WATER_DISCHARGE;
    }

    return batchStatusType;
  },

  onWaterScaleHold(this: IBatchStore, scale: IScale): void {
    if (this.isHoldScale(scale.id)) {
      const scaleStatus = this.getDeviceStatus(scale.id);
      const activeBatchData = this.running.length
        ? this.running.find((v: IBatchData) => v.uid === scaleStatus.uid)
        : null;

      if (activeBatchData) {
        activeBatchData.startWashWaterManually = !!(activeBatchData?.washWater
          && (!activeBatchData.headWater || activeBatchData?.headWaterScaleDischarged));
      }

      this.resumeScale(scale.id);

    } else {
      this.holdScale(scale);
    }
  },

};
